import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, RefreshCw, Phone, Clock, XCircle, User, Users, Check, X, PhoneCall, PhoneIncoming, PhoneOutgoing } from 'lucide-react';

// Configuration for Wazo API
const WAZO_CONFIG = {
  apiUrl: 'https://ipbx.telco.co.zw/api',
  credentials: {
    username: 'tawandac',
    password: 'Magik2pas41$$'
  },
  queueId: 3,
  queueName: 'NetworkIncidents'
};

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const WazoDashboard = () => {
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callStats, setCallStats] = useState({
    received: 0,
    answered: 0,
    abandoned: 0,
    not_answered: 0,
    average_waiting_time: 0,
    answered_rate: 0
  });
  const [currentCalls, setCurrentCalls] = useState([]);
  const [hourlyStats, setHourlyStats] = useState([]);
  const [queueStats, setQueueStats] = useState([]);
  const [agentStats, setAgentStats] = useState([]);
  const [agentStatus, setAgentStatus] = useState([]);
  const [selectedDate, setSelectedDate] = useState('today');
  const [dateFilter, setDateFilter] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Section-specific loading states
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingHourly, setLoadingHourly] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [loadingAgentStats, setLoadingAgentStats] = useState(true);
  const [loadingAgentStatus, setLoadingAgentStatus] = useState(true);
  const [loadingCurrentCalls, setLoadingCurrentCalls] = useState(true);

  // Helper function to format time ago
  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return `${interval} years ago`;
    
    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return `${interval} months ago`;
    
    interval = Math.floor(seconds / 86400);
    if (interval > 1) return `${interval} days ago`;
    
    interval = Math.floor(seconds / 3600);
    if (interval > 1) return `${interval} hours ago`;
    
    interval = Math.floor(seconds / 60);
    if (interval > 1) return `${interval} minutes ago`;
    
    return `${Math.floor(seconds)} seconds ago`;
  };

  // Function to authenticate with Wazo API
  const authenticate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Make a request to the Wazo API authentication endpoint
      const response = await fetch(`${WAZO_CONFIG.apiUrl}/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: WAZO_CONFIG.credentials.username,
          password: WAZO_CONFIG.credentials.password,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Authentication failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check if token is in the expected format
      if (!data.token) {
        throw new Error('Invalid response format: token not found');
      }
      
      setToken(data.token);
      setIsLoading(false);
    } catch (err) {
      // For development/testing, you can uncomment this line to use a mock token
      // setToken('dummy-token');
      
      setError(`107: Authentication failed: ${err.message}`);
      console.error('Authentication error:', err);
      setIsLoading(false);
    }
  };

  // Function to fetch call statistics from Wazo API
  const fetchCallStats = useCallback(async () => {
    if (!token) return;
    
    try {
      // Reset main loading state
      setIsLoading(true);
      
      // Reset section-specific loading states
      setLoadingSummary(true);
      setLoadingHourly(true);
      setLoadingDaily(true);
      setLoadingAgentStats(true);
      setLoadingAgentStatus(true);
      setLoadingCurrentCalls(true);
      
      // 1. Fetch queue statistics for summary data
      try {
        const queueStatsResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/call-logd/queues/${WAZO_CONFIG.queueId}/statistics?from=${dateFilter.start}T00:00:00&until=${dateFilter.end}T23:59:59`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!queueStatsResponse.ok) {
          throw new Error(`Failed to fetch queue stats: ${queueStatsResponse.status}`);
        }
        
        const queueStatsData = await queueStatsResponse.json();
        
        // Check if we have data in the response
        if (!queueStatsData || !queueStatsData.items || queueStatsData.items.length === 0) {
          console.warn('No queue statistics available for the selected date range.');
          setCallStats({
            received: 0,
            answered: 0,
            abandoned: 0,
            not_answered: 0,
            average_waiting_time: 0,
            answered_rate: 0
          });
        } else {
          // Use the most recent queue stats item
          const queueStats = queueStatsData.items[0];
          
          // Set the call stats from the API response
          setCallStats({
            received: queueStats.received || 0,
            answered: queueStats.answered || 0,
            abandoned: queueStats.abandoned || 0,
            not_answered: queueStats.not_answered || 0,
            average_waiting_time: queueStats.average_waiting_time || 0,
            answered_rate: queueStats.answered_rate || 0
          });
        }
      } catch (err) {
        console.error('Error fetching summary data:', err);
      } finally {
        setLoadingSummary(false);
      }
      
      // 2. Fetch hourly stats for the selected date range
      try {
        const hourlyStatsResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/call-logd/queues/${WAZO_CONFIG.queueId}/statistics/hour?from=${dateFilter.start}T00:00:00&until=${dateFilter.end}T23:59:59`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!hourlyStatsResponse.ok) {
          throw new Error(`Failed to fetch hourly stats: ${hourlyStatsResponse.status}`);
        }
        
        const hourlyStatsData = await hourlyStatsResponse.json();
        
        // Initialize with empty data for all hours
        const hourlyStats = Array(24).fill().map((_, hour) => ({
          hour,
          received: 0,
          answered: 0,
          abandoned: 0,
          not_answered: 0
        }));
        
        // Populate with actual data where available
        if (hourlyStatsData && hourlyStatsData.items && hourlyStatsData.items.length > 0) {
          hourlyStatsData.items.forEach(item => {
            const hour = new Date(item.time).getHours();
            hourlyStats[hour] = {
              hour,
              received: item.received || 0,
              answered: item.answered || 0,
              abandoned: item.abandoned || 0,
              not_answered: item.not_answered || 0
            };
          });
        }
        
        setHourlyStats(hourlyStats);
      } catch (err) {
        console.error('Error fetching hourly data:', err);
      } finally {
        setLoadingHourly(false);
      }
      
      // 3. Fetch daily stats for trend analysis (last 7 days)
      try {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
        const startDate = sevenDaysAgo.toISOString().split('T')[0];
        const endDate = new Date().toISOString().split('T')[0];
        
        const dailyStatsResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/call-logd/queues/${WAZO_CONFIG.queueId}/statistics/day?from=${startDate}T00:00:00&until=${endDate}T23:59:59`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!dailyStatsResponse.ok) {
          throw new Error(`Failed to fetch daily stats: ${dailyStatsResponse.status}`);
        }
        
        const dailyStatsData = await dailyStatsResponse.json();
        
        if (dailyStatsData && dailyStatsData.items && dailyStatsData.items.length > 0) {
          // Transform API data for the chart
          const queueData = dailyStatsData.items.map(item => {
            const date = new Date(item.time);
            const dayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            
            return {
              name: dayStr,
              received: item.received || 0,
              answered: item.answered || 0,
              abandoned: item.abandoned || 0,
              not_answered: item.not_answered || 0
            };
          });
          
          setQueueStats(queueData);
        } else {
          // If no data, create empty placeholders for the last 7 days
          const queueData = [];
          for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            
            queueData.push({
              name: dayStr,
              received: 0,
              answered: 0,
              abandoned: 0,
              not_answered: 0
            });
          }
          
          setQueueStats(queueData);
        }
      } catch (err) {
        console.error('Error fetching daily data:', err);
      } finally {
        setLoadingDaily(false);
      }
      
      // 4. Fetch agent statistics
      try {
        const agentStatsResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/call-logd/agents/statistics?from=${dateFilter.start}T00:00:00&until=${dateFilter.end}T23:59:59&qid=${WAZO_CONFIG.queueId}`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!agentStatsResponse.ok) {
          throw new Error(`Failed to fetch agent stats: ${agentStatsResponse.status}`);
        }
        
        const agentStatsData = await agentStatsResponse.json();
        
        if (agentStatsData && agentStatsData.items && agentStatsData.items.length > 0) {
          // Transform agent stats data
          const agentData = agentStatsData.items.map(agent => {
            // Calculate efficiency score based on call duration
            const efficiency = agent.average_conversation_time > 0 
              ? Math.max(0, Math.min(100, Math.round(90 - (agent.average_conversation_time / 10))))
              : 0;
              
            return {
              name: agent.agent_name || `Agent ${agent.agent_id}`,
              answered: agent.answered || 0,
              totalDuration: (agent.answered || 0) * (agent.average_conversation_time || 0),
              avgCallDuration: agent.average_conversation_time || 0,
              queue: WAZO_CONFIG.queueName,
              efficiency: efficiency
            };
          });
          
          setAgentStats(agentData);
        } else {
          // If no agent data is available, set an empty array
          setAgentStats([]);
        }
      } catch (err) {
        console.error('Error fetching agent stats:', err);
      } finally {
        setLoadingAgentStats(false);
      }
      
      // 5. Fetch agent status
      try {
        const agentStatusResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/confd/agents`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!agentStatusResponse.ok) {
          throw new Error(`Failed to fetch agent status: ${agentStatusResponse.status}`);
        }
        
        const agentListData = await agentStatusResponse.json();
        
        if (agentListData && agentListData.items && agentListData.items.length > 0) {
          // For each agent, fetch their status
          const agentStatusPromises = agentListData.items.map(async agent => {
            try {
              const statusResponse = await fetch(
                `${WAZO_CONFIG.apiUrl}/confd/agents/${agent.id}/status`,
                {
                  headers: {
                    'X-Auth-Token': token,
                    'Content-Type': 'application/json',
                  }
                }
              );
              
              if (!statusResponse.ok) {
                return null;
              }
              
              const statusData = await statusResponse.json();
              
              // Map Wazo status to our status categories
              let status;
              let statusClass;
              let statusIcon = null;
              
              if (statusData.logged) {
                if (statusData.paused) {
                  status = 'Break';
                  statusClass = 'bg-yellow-100 text-yellow-800';
                } else if (statusData.in_call) {
                  status = 'On Call';
                  statusClass = 'bg-blue-100 text-blue-800';
                } else {
                  status = 'Available';
                  statusClass = 'bg-green-100 text-green-800';
                  statusIcon = <Check size={16} className="text-green-500" />;
                }
              } else {
                status = 'Offline';
                statusClass = 'bg-gray-100 text-gray-800';
                statusIcon = <X size={16} className="text-gray-500" />;
              }
              
              return {
                name: agent.firstname ? `${agent.firstname} ${agent.lastname}` : `Agent ${agent.number}`,
                status: status,
                statusClass: statusClass,
                statusIcon: statusIcon,
                queue: WAZO_CONFIG.queueName,
                lastActivity: statusData.last_call ? timeAgo(new Date(statusData.last_call)) : 'N/A',
                extension: agent.number || 'N/A'
              };
            } catch (error) {
              console.error(`Error fetching status for agent ${agent.id}:`, error);
              return null;
            }
          });
          
          const agentStatusResults = await Promise.all(agentStatusPromises);
          setAgentStatus(agentStatusResults.filter(agent => agent !== null));
        } else {
          // If no agent data is available, set an empty array
          setAgentStatus([]);
        }
      } catch (err) {
        console.error('Error fetching agent status:', err);
      } finally {
        setLoadingAgentStatus(false);
      }
      
      // 6. Fetch current active calls
      try {
        const currentCallsResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/call-logd/cdr/queues/${WAZO_CONFIG.queueId}/current`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!currentCallsResponse.ok) {
          throw new Error(`Failed to fetch current calls: ${currentCallsResponse.status}`);
        }
        
        const currentCallsData = await currentCallsResponse.json();
        
        if (currentCallsData && currentCallsData.items) {
          // Transform current calls data
          const activeCalls = currentCallsData.items.map(call => {
            const isInbound = call.direction === 'inbound';
            const callDirection = isInbound ? 
              <PhoneIncoming size={16} className="text-green-500 mr-1" /> : 
              <PhoneOutgoing size={16} className="text-blue-500 mr-1" />;
              
            // Calculate minutes and seconds for display
            const minutes = Math.floor(call.duration / 60);
            const seconds = call.duration % 60;
            const durationFormatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;
            
            return {
              id: call.call_id,
              agent: call.agent_name || 'Waiting in queue',
              extension: call.extension || '-',
              callerId: call.caller_id_number,
              type: call.direction.charAt(0).toUpperCase() + call.direction.slice(1),
              direction: callDirection,
              duration: call.duration,
              durationFormatted: durationFormatted,
              waitTime: call.wait_time,
              status: call.agent_name ? 'In Progress' : 'Waiting'
            };
          });
          
          setCurrentCalls(activeCalls);
        } else {
          // If no current calls, set an empty array
          setCurrentCalls([]);
        }
      } catch (err) {
        console.error('Error fetching current calls:', err);
      } finally {
        setLoadingCurrentCalls(false);
      }
      
      // All section data has been fetched, reset main loading state
      setIsLoading(false);
    } catch (err) {
      setError(`Failed to fetch call statistics: ${err.message}`);
      console.error('API error:', err);
      setIsLoading(false);
      
      // Make sure all section loading states are reset
      setLoadingSummary(false);
      setLoadingHourly(false);
      setLoadingDaily(false);
      setLoadingAgentStats(false);
      setLoadingAgentStatus(false);
      setLoadingCurrentCalls(false);
    }
  }, [token, dateFilter, WAZO_CONFIG.apiUrl, WAZO_CONFIG.queueId, WAZO_CONFIG.queueName, timeAgo]);

  // Handle date filter changes
  const handleDateChange = (option) => {
    const today = new Date();
    let start, end;
    
    switch (option) {
      case 'today':
        start = new Date().toISOString().split('T')[0];
        end = start;
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = yesterday.toISOString().split('T')[0];
        end = start;
        break;
      case 'last7days':
        end = today.toISOString().split('T')[0];
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 6);
        start = lastWeek.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        start = firstDay.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      default:
        start = new Date().toISOString().split('T')[0];
        end = start;
    }
    
    setSelectedDate(option);
    setDateFilter({ start, end });
  };

  // Initialize authentication when component mounts
  useEffect(() => {
    authenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch call stats whenever the token or date filter changes
  useEffect(() => {
    if (token) {
      fetchCallStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dateFilter]);
  
  // Auto-refresh current calls every 5 seconds
  useEffect(() => {
    if (!token) return;
    
    const refreshCurrentCalls = async () => {
      try {
        setLoadingCurrentCalls(true);
        
        const currentCallsResponse = await fetch(
          `${WAZO_CONFIG.apiUrl}/call-logd/cdr/queues/${WAZO_CONFIG.queueId}/current`,
          {
            headers: {
              'X-Auth-Token': token,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!currentCallsResponse.ok) {
          throw new Error(`Failed to fetch current calls: ${currentCallsResponse.status}`);
        }
        
        const currentCallsData = await currentCallsResponse.json();
        
        if (currentCallsData && currentCallsData.items) {
          // Transform current calls data
          const activeCalls = currentCallsData.items.map(call => {
            const isInbound = call.direction === 'inbound';
            const callDirection = isInbound ? 
              <PhoneIncoming size={16} className="text-green-500 mr-1" /> : 
              <PhoneOutgoing size={16} className="text-blue-500 mr-1" />;
              
            // Calculate minutes and seconds for display
            const minutes = Math.floor(call.duration / 60);
            const seconds = call.duration % 60;
            const durationFormatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;
            
            return {
              id: call.call_id,
              agent: call.agent_name || 'Waiting in queue',
              extension: call.extension || '-',
              callerId: call.caller_id_number,
              type: call.direction.charAt(0).toUpperCase() + call.direction.slice(1),
              direction: callDirection,
              duration: call.duration,
              durationFormatted: durationFormatted,
              waitTime: call.wait_time,
              status: call.agent_name ? 'In Progress' : 'Waiting'
            };
          });
          
          setCurrentCalls(activeCalls);
        } else {
          // If no current calls, set an empty array
          setCurrentCalls([]);
        }
      } catch (err) {
        console.error('Error refreshing current calls:', err);
      } finally {
        setLoadingCurrentCalls(false);
      }
    };
    
    // Initial fetch
    refreshCurrentCalls();
    
    // Set up interval for auto-refresh
    const interval = setInterval(() => {
      refreshCurrentCalls();
    }, 5000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, WAZO_CONFIG.apiUrl, WAZO_CONFIG.queueId]);

  // Calculate total calls
  const totalCalls = callStats.received;
  
  // Prepare data for the pie chart
  const pieData = [
    { name: 'Answered', value: callStats.answered },
    { name: 'Abandoned', value: callStats.abandoned },
    { name: 'Not Answered', value: callStats.not_answered }
  ];

  // Refresh data
  const handleRefresh = () => {
    fetchCallStats();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto">
          <h1 className="text-2xl font-bold">NetworkIncidents Queue Dashboard</h1>
        </div>
      </header>

      <div className="container mx-auto p-4 flex-1">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded-lg shadow">
          <div className="flex items-center mb-4 md:mb-0">
            <Calendar className="mr-2 text-blue-600" />
            <div>
              <label className="font-medium text-gray-700">Date Range:</label>
              <div className="mt-1">
                <select 
                  value={selectedDate} 
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="thisMonth">This Month</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <div className="text-sm text-gray-600">
              {dateFilter.start === dateFilter.end 
                ? `Data for: ${dateFilter.start}` 
                : `Data from: ${dateFilter.start} to ${dateFilter.end}`}
            </div>
            <button 
              onClick={handleRefresh} 
              className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200"
              title="Refresh Data"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              {loadingSummary ? (
                <div className="col-span-4 flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <>
                  <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center mb-4">
                      <Phone className="text-green-500 mr-2" />
                      <h2 className="text-lg font-semibold">Answered Calls</h2>
                    </div>
                    <div className="text-3xl font-bold text-green-500">{callStats.answered}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {totalCalls > 0 ? Math.round((callStats.answered / totalCalls) * 100) : 0}% of total calls
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center mb-4">
                      <XCircle className="text-red-500 mr-2" />
                      <h2 className="text-lg font-semibold">Abandoned Calls</h2>
                    </div>
                    <div className="text-3xl font-bold text-red-500">{callStats.abandoned}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {totalCalls > 0 ? Math.round((callStats.abandoned / totalCalls) * 100) : 0}% of total calls
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center mb-4">
                      <Clock className="text-yellow-500 mr-2" />
                      <h2 className="text-lg font-semibold">Not Answered</h2>
                    </div>
                    <div className="text-3xl font-bold text-yellow-500">{callStats.not_answered}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {totalCalls > 0 ? Math.round((callStats.not_answered / totalCalls) * 100) : 0}% of total calls
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center mb-4">
                      <Clock className="text-blue-500 mr-2" />
                      <h2 className="text-lg font-semibold">Avg. Wait Time</h2>
                    </div>
                    <div className="text-3xl font-bold text-blue-500">{callStats.average_waiting_time} sec</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Answer rate: {Math.round(callStats.answered_rate)}%
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-4">Call Distribution</h2>
                {loadingSummary ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} calls`, null]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-4">Hourly Call Volume</h2>
                {loadingHourly ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={hourlyStats}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottomRight', offset: -10 }} />
                        <YAxis label={{ value: 'Number of Calls', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value) => [`${value} calls`, null]} />
                        <Legend />
                        <Line type="monotone" dataKey="received" stroke="#8884d8" name="Received" />
                        <Line type="monotone" dataKey="answered" stroke="#00C49F" name="Answered" />
                        <Line type="monotone" dataKey="abandoned" stroke="#FF8042" name="Abandoned" />
                        <Line type="monotone" dataKey="not_answered" stroke="#FFBB28" name="Not Answered" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow mb-6">
              <h2 className="text-lg font-semibold mb-4">Daily Call Statistics - NetworkIncidents Queue</h2>
              {loadingDaily ? (
                <div className="flex justify-center items-center h-80">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={queueStats}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value} calls`, null]} />
                      <Legend />
                      <Bar dataKey="received" name="Received" fill="#8884d8" />
                      <Bar dataKey="answered" name="Answered" fill="#00C49F" />
                      <Bar dataKey="abandoned" name="Abandoned" fill="#FF8042" />
                      <Bar dataKey="not_answered" name="Not Answered" fill="#FFBB28" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <PhoneCall className="text-blue-600 mr-2" />
                    <h2 className="text-lg font-semibold">Current Active Calls</h2>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-2">Auto-refreshes every 5s</span>
                    <button 
                      onClick={handleRefresh} 
                      className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200"
                      title="Refresh Now"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                
                {currentCalls.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <Phone className="mb-2 text-gray-400" size={40} />
                    <p>No active calls at the moment</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caller ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ext</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wait</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentCalls.map((call, index) => (
                          <tr key={call.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {call.direction}
                                <span className={`text-xs ${
                                  call.status === 'In Progress' ? 'text-green-600' : 'text-yellow-600'
                                }`}>
                                  {call.status}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{call.callerId}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {call.agent}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{call.extension}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {call.durationFormatted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {call.waitTime > 0 ? `${call.waitTime}s` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center mb-4">
                  <User className="text-blue-600 mr-2" />
                  <h2 className="text-lg font-semibold">Agent Call Statistics</h2>
                </div>
                
                {loadingAgentStats ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : agentStats.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <User className="mb-2 text-gray-400" size={40} />
                    <p>No agent statistics available</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calls Handled</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Duration</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Efficiency</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {agentStats.map((agent, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{agent.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{agent.answered}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {Math.floor(agent.avgCallDuration / 60)}m {agent.avgCallDuration % 60}s
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${agent.efficiency}%` }}></div>
                              </div>
                              <span className="text-xs text-gray-500">{agent.efficiency}%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow mb-6">
              <div className="flex items-center mb-4">
                <Users className="text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold">Agent Availability</h2>
              </div>
              
              {loadingAgentStatus ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : agentStatus.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Users className="mb-2 text-gray-400" size={40} />
                  <p>No agent status information available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extension</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {agentStatus.map((agent, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{agent.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${agent.statusClass}`}>
                              {agent.statusIcon && <span className="mr-1">{agent.statusIcon}</span>}
                              {agent.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{agent.extension}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{agent.lastActivity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <footer className="bg-gray-800 text-white p-4 text-center">
        <p className="text-sm">NetworkIncidents Queue Dashboard © {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
};

export default WazoDashboard;
